import React, { useState } from "react";

function RiskAssessment() {
  const [riskAnswers, setRiskAnswers] = useState({
    age: "",
    sport: "",
    position: "",
    concussionHistory: "",
    multipleConcussions: "",
    symptomDuration: "",
    symptoms: "",
    protectiveGear: "",
    historyHeadTrauma: "",
  });

  const [riskLevel, setRiskLevel] = useState(null);
  const [riskExplanation, setRiskExplanation] = useState("");
  const [error, setError] = useState("");

  const handleChange = (event) => {
    setRiskAnswers({
      ...riskAnswers,
      [event.target.name]: event.target.value,
    });
  };

  const calculateRisk = () => {
    setError("");
    setRiskLevel(null);
    setRiskExplanation("");

    // Ensure all fields are completed
    for (let key in riskAnswers) {
      if (!riskAnswers[key]) {
        setError("All fields must be completed before calculating risk.");
        return;
      }
    }

    let score = 0;
    let explanation = [];

    // ✅ Age-Based Risk
    if (riskAnswers.age === "under18") {
      score += 3;
      explanation.push("Young athletes have a higher susceptibility to concussions due to ongoing brain development.");
    }
    if (riskAnswers.age === "18-35") {
      score += 2;
      explanation.push("Athletes in this age group have developed brains but still face significant concussion risks.");
    }

    // ✅ Sport-Based Risk
    const highRiskSports = ["football", "hockey", "rugby", "lacrosse"];
    if (highRiskSports.includes(riskAnswers.sport)) {
      score += 4;
      explanation.push(`${riskAnswers.sport} is a high-contact sport, increasing the risk of head trauma.`);
    }

    // ✅ Position-Based Risk
    const highImpactPositions = ["lineman", "goalkeeper", "defender"];
    if (highImpactPositions.includes(riskAnswers.position)) {
      score += 3;
      explanation.push(`As a ${riskAnswers.position}, you are more likely to experience high-impact collisions.`);
    }

    // ✅ Concussion History
    if (riskAnswers.concussionHistory === "yes") {
      score += 5;
      explanation.push("A history of concussions increases the likelihood of long-term effects and future injuries.");
    }

    // ✅ Multiple Concussions
    if (riskAnswers.multipleConcussions === "yes") {
      score += 5;
      explanation.push("Repeated concussions dramatically increase the risk of chronic traumatic encephalopathy (CTE).");
    }

    // ✅ Symptom Duration
    if (riskAnswers.symptomDuration === "over7days") {
      score += 4;
      explanation.push("Symptoms lasting over a week indicate potential post-concussion syndrome.");
    }

    // ✅ Current Symptoms
    if (riskAnswers.symptoms === "yes") {
      score += 3;
      explanation.push("Experiencing symptoms like dizziness, nausea, or headaches suggests a possible ongoing concussion.");
    }

    // ✅ Protective Gear Use
    if (riskAnswers.protectiveGear === "no") {
      score += 3;
      explanation.push("Lack of proper protective gear increases vulnerability to head injuries.");
    }

    // ✅ History of Other Head Trauma
    if (riskAnswers.historyHeadTrauma === "yes") {
      score += 2;
      explanation.push("A history of previous head injuries increases sensitivity to future concussions.");

    }

    // ✅ Final Risk Categorization
    if (score >= 15) {
      setRiskLevel("High Risk - Immediate Medical Evaluation Recommended");
    } else if (score >= 8) {
      setRiskLevel("Moderate Risk - Close Monitoring Required");
    } else {
      setRiskLevel("Low Risk - No Immediate Signs of Concern");
    }

    setRiskExplanation(explanation.join(" "));
  };

  return (
    <div className="assessment-form">
      <h2>Advanced Risk Assessment</h2>

      <label>What is your age group?</label>
      <select name="age" onChange={handleChange}>
        <option value="">Select Age Group</option>
        <option value="under18">Under 18</option>
        <option value="18-35">18-35</option>
        <option value="35plus">35+</option>
      </select>

      <label>Which sport or profession are you in?</label>
      <select name="sport" onChange={handleChange}>
        <option value="">Select Sport</option>
        <option value="football">Football</option>
        <option value="hockey">Hockey</option>
        <option value="rugby">Rugby</option>
        <option value="lacrosse">Lacrosse</option>
        <option value="military">Military</option>
        <option value="other">Other</option>
      </select>

      <label>What position do you play?</label>
      <select name="position" onChange={handleChange}>
        <option value="">Select Position</option>
        <option value="lineman">Lineman</option>
        <option value="goalkeeper">Goalkeeper</option>
        <option value="defender">Defender</option>
        <option value="midfielder">Midfielder</option>
        <option value="attacker">Attacker</option>
        <option value="other">Other</option>
      </select>

      <label>Have you had a concussion before?</label>
      <select name="concussionHistory" onChange={handleChange}>
        <option value="">Select Option</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>

      <label>Have you had multiple concussions?</label>
      <select name="multipleConcussions" onChange={handleChange}>
        <option value="">Select Option</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>

      <label>How long have your symptoms lasted?</label>
      <select name="symptomDuration" onChange={handleChange}>
        <option value="">Select Duration</option>
        <option value="less3days">Less than 3 days</option>
        <option value="4-7days">4-7 days</option>
        <option value="over7days">More than 7 days</option>
      </select>

      <label>Are you currently experiencing symptoms like dizziness, headache, nausea?</label>
      <select name="symptoms" onChange={handleChange}>
        <option value="">Select Option</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>

      <label>Do you wear protective headgear?</label>
      <select name="protectiveGear" onChange={handleChange}>
        <option value="">Select Option</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>

      <label>Have you ever experienced other head trauma?</label>
      <select name="historyHeadTrauma" onChange={handleChange}>
        <option value="">Select Option</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>

      {error && <p className="error-message">{error}</p>}
      <button onClick={calculateRisk}>Calculate Risk</button>
      {riskLevel && <p className="risk-result">{riskLevel}</p>}
      {riskExplanation && <p className="risk-explanation">{riskExplanation}</p>}
    </div>
  );
}

export default RiskAssessment;